import { IAnalyticsUserInfo } from '@/types/shared.types';
import { SETTINGS_ANALYTICS_EVENTS } from '@/utils/constants';
import { useCurrentUserStore } from '@/services/auth/store';
import { usePostHog } from 'posthog-js/react';
import type { ForumDigestSettings } from '@/services/forum/hooks/useGetForumDigestSettings';

export type ForumDigestAnalyticsSource = 'home-feed' | 'settings';

/** How a member reached News preferences (the page's `from` param). */
export type NewsPreferencesArrival = 'digest' | 'feed' | 'settings';

export interface ForumDigestOptionSelectParams extends ForumDigestSettings {
  source: ForumDigestAnalyticsSource;
}

export const useSettingsAnalytics = () => {
  const postHogProps = usePostHog();

  const captureEvent = (eventName: string, eventParams = {}) => {
    try {
      if (postHogProps?.capture) {
        const allParams = { ...eventParams };
        const userInfo = useCurrentUserStore.getState().currentUser;
        const loggedInUserUid = userInfo?.uid;
        const loggedInUserEmail = userInfo?.email;
        const loggedInUserName = userInfo?.name;
        postHogProps.capture(eventName, { ...allParams, loggedInUserUid, loggedInUserEmail, loggedInUserName });
      }
    } catch (e) {
      console.error(e);
    }
  };

  function recordSettingsSideMenuClick(menuName: string, url: string, user: IAnalyticsUserInfo | null) {
    const params = {
      menuName,
      url,
      user,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_SIDE_MENU_CLICK, params);
  }

  function recordManageTeamsTeamChange(team: any, user: IAnalyticsUserInfo | null) {
    const params = {
      ...team,
      user,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_MANAGE_TEAMS_TEAM_CHANGE, params);
  }

  function recordManageTeamSave(type: string, user: IAnalyticsUserInfo | null, payload?: any) {
    const params = {
      type,
      user,
      ...payload,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_MANAGE_TEAMS_SAVE, params);
  }

  function recordManageMembersMemberChange(member: any, user: IAnalyticsUserInfo | null) {
    const params = {
      ...member,
      user,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_MANAGE_MEMBERS_MEMBER_CHANGE, params);
  }

  function recordManageMemberSave(type: string, user: IAnalyticsUserInfo | null, payload?: any) {
    const params = {
      type,
      user,
      ...payload,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_MANAGE_MEMBERS_SAVE, params);
  }

  function recordMemberProfileSave(type: string, user: IAnalyticsUserInfo | null, payload?: any) {
    const params = {
      type,
      user,
      ...payload,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_MEMBER_PROFILE_SAVE, params);
  }

  function recordMemberEmailAdminEditClick(email: string, user: IAnalyticsUserInfo | null) {
    const params = {
      email,
      user,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_MEMBER_EMAIL_ADMIN_EDIT_CLICK, params);
  }

  function recordMemberEmailAdminEditCancel(email: string, user: IAnalyticsUserInfo | null) {
    const params = {
      email,
      user,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_MEMBER_EMAIL_ADMIN_EDIT_CANCEL, params);
  }

  function recordMemberEmailAdminEditSuccess(user: IAnalyticsUserInfo | null) {
    const params = {
      user,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_MEMBER_EMAIL_ADMIN_EDIT_SUCCESS, params);
  }

  function recordMemberPreferenceChange(type: string, user: IAnalyticsUserInfo | null, payload?: any) {
    const params = {
      type,
      user,
      ...payload,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_USER_PREFERENCES, params);
  }

  function recordMemberPreferenceReset(user: IAnalyticsUserInfo | null) {
    const params = {
      user,
      name: 'reset',
      value: true,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_USER_PREFERENCES_RESET, params);
  }

  function recordMemberProfileFormEdit(user: IAnalyticsUserInfo | null, tabName: string) {
    const params = {
      user,
      name: tabName,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_MEMBER_PROFILE_EDIT_FORM, params);
  }

  function recordUserProfileFormEdit(user: IAnalyticsUserInfo | null, tabName: string) {
    const params = {
      user,
      name: tabName,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_USER_PROFILE_EDIT_FORM, params);
  }

  function recordTeamProfileFormEdit(user: IAnalyticsUserInfo | null, tabName: string) {
    const params = {
      user,
      itemName: tabName,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_TEAM_PROFILE_EDIT_FORM, params);
  }

  function recordMemberProjectContributionAdd(type: string, user: IAnalyticsUserInfo | null) {
    const params = {
      user,
      type,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.PR_CONRTIBUTIONS_LIST_ITEM_ADD, params);
  }

  function recordMemberProjectContributionDelete(type: string, user: IAnalyticsUserInfo | null) {
    const params = {
      user,
      type,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.PR_CONRTIBUTIONS_LIST_ITEM_DELETE, params);
  }

  function recordMemberProjectContributionAddProject(type: string, user: IAnalyticsUserInfo | null) {
    const params = {
      user,
      type,
    };
    captureEvent(SETTINGS_ANALYTICS_EVENTS.PR_CONRTIBUTIONS_LIST_ITEM_ADDPROJECT, params);
  }

  function recordManageTeamsMemberTeamLeadToggleChange(params: any) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.MANAGE_TEAMS_MEMBER_TEAM_LEAD_TOGGLE_CHANGE, params);
  }

  function recordManageTeamsMemberRemove(params: any) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.MANAGE_TEAMS_MEMBER_REMOVE, params);
  }

  function recordManageTeamsMemberUndo(params: any) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.MANAGE_TEAMS_MEMBER_UNDO, params);
  }

  function onRecommendationsPageOpenFromMail(params: any) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_RECOMMENDATIONS_PAGE_OPEN_FROM_MAIL, params);
  }

  function onRecommendationsSettingsSaveClicked(params: any) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_RECOMMENDATIONS_SAVE_CLICKED, params);
  }

  function onRecommendationsSettingsResetClicked() {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_RECOMMENDATIONS_RESET_CLICKED);
  }

  function onRecommendationEmailFeedbackClicked(params: any) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.RECOMMENDATION_EMAIL_FEEDBACK_CLICKED, params);
  }

  function onForumDigestOptionSelect(params: ForumDigestOptionSelectParams) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.FORUM_DIGEST_OPTION_SELECTED, params);
  }

  function onForumDigestSaveFailed(params: { attemptedFrequency: string; source: ForumDigestAnalyticsSource }) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.FORUM_DIGEST_SAVE_FAILED, params);
  }

  function onForumDigestNetworkNewsToggleClicked(params: Record<any, boolean>) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.FORUM_DIGEST_NETWORK_NEWS_TOGGLE_CLICKED, params);
  }

  function onForumDigestForumActivityToggleClicked(params: Record<any, boolean>) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.FORUM_DIGEST_FORUM_ACTIVITY_TOGGLE_CLICKED, params);
  }

  function onSubscribeToPlNewsletterChange(params: Record<any, boolean>) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_SUBSCRIBE_TO_NEWSLETTER_CHANGE, params);
  }

  function onSubscribeToDemoDayUpdatesChange(params: Record<any, boolean>) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_SUBSCRIBE_TO_DEMO_DAY_UPDATES_CHANGE, params);
  }

  function onDemoDayUpdatesNotificationToggleClicked(params: Record<any, boolean>) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_DEMO_DAY_UPDATES_NOTIFICATION_TOGGLE_CLICKED, params);
  }

  function onSuggestedCandidatesEmailToggleClicked(params: { suggestedCandidatesEmailEnabled: boolean }) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_SUGGESTED_CANDIDATES_EMAIL_TOGGLE_CLICKED, params);
  }

  function onEmailChangePanelOpened() {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_EMAIL_CHANGE_PANEL_OPENED);
  }

  function onEmailChangeCancelled() {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_EMAIL_CHANGE_CANCELLED);
  }

  // News preferences (LAB-2941)
  function onNewsPreferencesOpened(from: NewsPreferencesArrival) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_NEWS_PREFERENCES_OPENED, { from });
  }

  function onNewsPreferencesTagToggled(params: { tag: string; on: boolean; from: NewsPreferencesArrival }) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_NEWS_PREFERENCES_TAG_TOGGLED, params);
  }

  function onNewsPreferencesSaveFailed(params: { section: 'tags' | 'teams'; teamUid?: string }) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_NEWS_PREFERENCES_SAVE_FAILED, params);
  }

  function onNewsPreferencesFollowToggled(params: {
    teamUid: string;
    following: boolean;
    from: NewsPreferencesArrival;
    /** True when the team was one of the suggestions shown to a member who follows none (LAB-3037). */
    suggested?: boolean;
  }) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_NEWS_PREFERENCES_FOLLOW_TOGGLED, params);
  }

  /** When the member leaves the tag search with text in it (LAB-3023). */
  function onNewsPreferencesTagSearched(params: { query: string; matches: number }) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_NEWS_PREFERENCES_TAG_SEARCHED, params);
  }

  /** "Show N more" on a long theme (LAB-3039); not sent for "Show less". */
  function onNewsPreferencesThemeExpanded(params: { theme: string; from: NewsPreferencesArrival }) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_NEWS_PREFERENCES_GROUP_EXPANDED, params);
  }

  /** "For You" in the page intro, which opens the For You feed on Home (LAB-3036). */
  function onNewsPreferencesForYouLinkClicked(from: NewsPreferencesArrival) {
    captureEvent(SETTINGS_ANALYTICS_EVENTS.SETTINGS_NEWS_PREFERENCES_FOR_YOU_LINK_CLICKED, { from });
  }

  return {
    onNewsPreferencesOpened,
    onNewsPreferencesTagToggled,
    onNewsPreferencesSaveFailed,
    onNewsPreferencesFollowToggled,
    onNewsPreferencesTagSearched,
    onNewsPreferencesThemeExpanded,
    onNewsPreferencesForYouLinkClicked,
    recordSettingsSideMenuClick,
    recordManageTeamsTeamChange,
    recordManageTeamSave,
    recordManageMembersMemberChange,
    recordManageMemberSave,
    recordMemberProfileSave,
    recordMemberEmailAdminEditClick,
    recordMemberEmailAdminEditCancel,
    recordMemberPreferenceChange,
    recordMemberPreferenceReset,
    recordMemberProfileFormEdit,
    recordUserProfileFormEdit,
    recordTeamProfileFormEdit,
    recordMemberProjectContributionAdd,
    recordMemberProjectContributionDelete,
    recordMemberProjectContributionAddProject,
    recordManageTeamsMemberTeamLeadToggleChange,
    recordManageTeamsMemberRemove,
    recordManageTeamsMemberUndo,
    onRecommendationsPageOpenFromMail,
    onRecommendationsSettingsSaveClicked,
    onRecommendationsSettingsResetClicked,
    onRecommendationEmailFeedbackClicked,
    onForumDigestOptionSelect,
    onForumDigestSaveFailed,
    onForumDigestNetworkNewsToggleClicked,
    onForumDigestForumActivityToggleClicked,
    onSubscribeToPlNewsletterChange,
    onSubscribeToDemoDayUpdatesChange,
    onDemoDayUpdatesNotificationToggleClicked,
    onSuggestedCandidatesEmailToggleClicked,
    onEmailChangePanelOpened,
    onEmailChangeCancelled,
  };
};
